import { useEffect, useState } from "react";

import { config } from "shared/data";

type SentinelEntry = {
  element: HTMLDivElement;
  label?: string;
  observer: IntersectionObserver;
  subscribers: Set<Subscriber>;
};

type Subscriber = (isOver: boolean) => void;

/**
 * Module-level registry keyed by threshold (px). One IntersectionObserver + one
 * sentinel DOM node per distinct threshold value; subscribers of the same
 * threshold share the same observer. Reference-counted dispose: the observer
 * is disconnected and the sentinel removed when the last subscriber unmounts.
 */
const sentinelRegistry = new Map<number, SentinelEntry>();

const supportsIntersectionObserver =
  typeof window !== "undefined" && typeof IntersectionObserver === "function";

// Dev affordance: load the page with ?debug-sentinels=1 to visualise where the
// scroll sentinels sit (threshold + bottom). Gated by environment via
// config.general.env (NEXT_PUBLIC_ENV_SLUG, e.g. "local" / "staging" / "qa" /
// "preprod" / "production"): disabled on production / preprod — staging / qa /
// local keep the overlay.
const isProductionEnvironment = ["preprod", "production"].includes(config.general.env);

// `typeof window` guard stays: the env check alone is SSR-safe (NEXT_PUBLIC_* is
// inlined at build time), but reading window.location must not happen while the
// module is evaluated on the server.
const debugSentinelVisuals =
  typeof window !== "undefined" &&
  !isProductionEnvironment &&
  new URLSearchParams(window.location.search).has("debug-sentinels");

const applyDebugSentinelVisuals = (element: HTMLDivElement): void => {
  if (!debugSentinelVisuals) return;
  // Full-width one-pixel band across the viewport at the sentinel's Y position.
  // Geometry note: only `width` changes — the vertical bounding-box height stays
  // 1px, so the IntersectionObserver flip points are unchanged; the box-shadow
  // adds visual thickness without touching the bounding box.
  element.style.width = "100%";
  element.style.left = "0";
  element.style.background = "#D93025";
  element.style.zIndex = "9999";
  element.style.boxShadow = "0 0 0 2px rgba(217, 48, 37, 0.85)";
};

// Debug-only chip describing the sentinel's purpose. Rendered as an absolutely
// positioned child of the sentinel — children never change the target's own
// bounding box, so the IntersectionObserver reads the same 1px geometry.
// pointer-events/user-select are re-enabled so the text can be highlighted
// and copied straight from the page.
const applyDebugSentinelLabel = (element: HTMLDivElement, label?: string): void => {
  if (!debugSentinelVisuals || !label) return;
  const chip = document.createElement("span");
  chip.textContent = label;
  chip.style.cssText =
    "position:absolute;left:12px;top:3px;font:600 11px/1.3 'SF Mono',Menlo,monospace;" +
    "color:#1C1917;background:rgba(255,255,255,0.94);border:1px solid #E6E4DD;" +
    "border-radius:4px;padding:2px 8px;white-space:nowrap;pointer-events:auto;" +
    "cursor:text;user-select:text;-webkit-user-select:text;z-index:10000;";
  element.append(chip);
};

/**
 * `entry.isIntersecting` alone cannot express on WHICH side of the viewport the
 * sentinel left: "scrolled past" (above the top edge) vs "not yet arrived"
 * (below the fold, never intersected). `boundingClientRect.top` closes the gap:
 * a negative top means the sentinel sits above the viewport top — truly passed.
 * Without this, the bottom sentinel (maxY, usually below the fold) and any
 * threshold larger than the viewport height would read as "over" at mount.
 */
const sentinelHasBeenPassed = (entry: IntersectionObserverEntry): boolean =>
  !entry.isIntersecting && entry.boundingClientRect.top < 0;

const readIsOver = (thresholdPx: number): boolean => {
  if (typeof window === "undefined") return false;
  return window.scrollY > thresholdPx;
};

const createSentinel = (thresholdPx: number): HTMLDivElement => {
  const element = document.createElement("div");
  element.dataset.scrollSentinel = String(thresholdPx);
  element.setAttribute("aria-hidden", "true");
  element.style.cssText =
    "position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none;" +
    `transform:translateY(${thresholdPx}px);`;
  applyDebugSentinelVisuals(element);
  document.body.append(element);
  return element;
};

const getOrCreateEntry = (thresholdPx: number, label?: string): SentinelEntry => {
  const existing = sentinelRegistry.get(thresholdPx);
  if (existing) return existing;

  const element = createSentinel(thresholdPx);
  applyDebugSentinelLabel(element, label);
  const subscribers = new Set<Subscriber>();
  const observer = new IntersectionObserver(
    entries => {
      const entry = entries[0];
      if (!entry) return;
      // Sentinel leaving the viewport upward means the user has scrolled past the
      // threshold. The bounding-rect check distinguishes "past" from "below the
      // fold" (threshold > viewport height) — see sentinelHasBeenPassed.
      const isOver = sentinelHasBeenPassed(entry);
      subscribers.forEach(callback => callback(isOver));
    },
    { threshold: 0 },
  );
  observer.observe(element);

  const entry: SentinelEntry = { element, label, observer, subscribers };
  sentinelRegistry.set(thresholdPx, entry);
  return entry;
};

const releaseEntry = (thresholdPx: number, subscriber: Subscriber) => {
  const entry = sentinelRegistry.get(thresholdPx);
  if (!entry) return;

  entry.subscribers.delete(subscriber);
  if (entry.subscribers.size === 0) {
    entry.observer.disconnect();
    entry.element.remove();
    sentinelRegistry.delete(thresholdPx);
  }
};

// region — window bottom sentinel (SCMPWEB-10011 T2-B)

type BottomEntry = {
  element: HTMLDivElement;
  label?: string;
  observer: IntersectionObserver;
  resizeObservers: ResizeObserver[];
  subscribers: Set<Subscriber>;
};

let bottomReference: undefined | { entry: BottomEntry };

// The sentinel sits ONE PIXEL ABOVE the document end (`scrollHeight − 1`) so its
// bottom edge is flush with the page end: the takeover bar must hide at exactly
// the master condition `scrollY + clientHeight >= scrollHeight`, i.e. the moment
// the viewport's bottom edge reaches the page's last pixel. Placing it AT
// `scrollHeight` instead would extend the scrollable overflow beyond the content
// on every reposition (+1px loop), and placing it at `maxY` (= scroll range end)
// would make it un-exit-able above the viewport — neither behaviour can fire.
// Recomputed whenever the document (content changes) or the viewport
// (`clientHeight` changes) resizes — both event-driven ResizeObserver callbacks,
// never per-frame scroll work.
const updateBottomSentinelPosition = (element: HTMLDivElement): void => {
  const { scrollHeight } = document.documentElement;
  const position = Math.max(0, scrollHeight - 1);
  const transform = `translateY(${position}px)`;
  // Idempotent: skip no-op rewrites so the IntersectionObserver is not churned.
  if (element.style.transform !== transform) element.style.transform = transform;
};

const readBottomReached = (): boolean => {
  if (typeof window === "undefined") return false;
  // Mirrors the master formula exactly: `scrollY + clientHeight >= scrollHeight`.
  return (
    window.scrollY + document.documentElement.clientHeight >= document.documentElement.scrollHeight
  );
};

const attachBottomResizeWatchers = (element: HTMLDivElement): ResizeObserver[] => {
  const reposition = (): void => updateBottomSentinelPosition(element);
  // body: covers document height changes (lazy-loaded content, widget mounts).
  // documentElement: covers viewport height changes (rotation, DevTools, resize).
  const bodyObserver = new ResizeObserver(reposition);
  const viewportObserver = new ResizeObserver(reposition);
  bodyObserver.observe(document.body);
  viewportObserver.observe(document.documentElement);
  // Position the sentinel BEFORE it is observed so no premature mismatched flip.
  reposition();
  return [bodyObserver, viewportObserver];
};

const getOrCreateBottomEntry = (label?: string): BottomEntry => {
  if (bottomReference) return bottomReference.entry;

  const element = document.createElement("div");
  element.dataset.scrollSentinel = "bottom";
  element.setAttribute("aria-hidden", "true");
  element.style.cssText =
    "position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none;";
  applyDebugSentinelVisuals(element);
  applyDebugSentinelLabel(element, label);
  const subscribers = new Set<Subscriber>();
  const resizeObservers = attachBottomResizeWatchers(element);
  const observer = new IntersectionObserver(
    entries => {
      const entry = entries[0];
      if (!entry) return;
      // The sentinel sits flush with the page end: it ENTERS the viewport exactly
      // when the viewport's bottom edge reaches the page's last pixel — the master
      // condition `scrollY + clientHeight >= scrollHeight`. No direction
      // discrimination needed here (unlike the threshold sentinel): the scroll
      // range clamps at that point, so "exited above the viewport" is impossible.
      const reached = entry.isIntersecting;
      subscribers.forEach(callback => callback(reached));
    },
    { threshold: 0 },
  );
  document.body.append(element);
  observer.observe(element);
  bottomReference = { entry: { element, label, observer, resizeObservers, subscribers } };
  return bottomReference.entry;
};

const releaseBottomEntry = (subscriber: Subscriber): void => {
  const ref = bottomReference;
  if (!ref) return;

  ref.entry.subscribers.delete(subscriber);
  if (ref.entry.subscribers.size === 0) {
    ref.entry.observer.disconnect();
    ref.entry.resizeObservers.forEach(resizeObserver => resizeObserver.disconnect());
    ref.entry.element.remove();
    bottomReference = undefined;
  }
};

/**
 * Tracks whether the window has been scrolled to (past) the bottom of the
 * document — `scrollY >= scrollHeight − viewportHeight`.
 *
 * Complements `useScrollOverThreshold` as the second half of a
 * threshold-and-bottom state machine (e.g. the takeover ad showing between a
 * scroll threshold and the page bottom). Implemented with a single shared
 * sentinel whose position is recomputed on document / viewport resizes —
 * zero window scroll listeners. SSR-safe (`false` on the server) and mirrors
 * `useScrollOverThreshold`'s 1px sampling semantics.
 */
export const useWindowBottomReached = (label?: string): boolean => {
  const [reached, setReached] = useState<boolean>(false);

  useEffect(() => {
    if (typeof window === "undefined" || !supportsIntersectionObserver) return;

    // Mirror `useScrollOverThreshold`'s mount-time sync (microtask-deferred to
    // avoid setState directly inside the effect body).
    queueMicrotask(() => setReached(readBottomReached()));

    const entry = getOrCreateBottomEntry(label);
    entry.subscribers.add(setReached);

    return (): void => releaseBottomEntry(setReached);
  }, [label]);

  return reached;
};

// endregion

/**
 * Tracks whether the window has been scrolled past the given threshold (px).
 *
 * Uses a shared sentinel-based IntersectionObserver instead of a `window`
 * scroll listener, so intersection computation happens off the main-thread
 * scroll critical path and there are zero per-frame JS scroll callbacks.
 * Same-threshold consumers across the page share a single observer.
 *
 * Pass `null` (or a non-positive threshold) while the real threshold is not
 * known yet (e.g. the measured height is still 0, or the element never becomes
 * sticky): the hook then stays `false` and creates **no** sentinel — once a
 * positive threshold arrives, the sentinel is created and subscribed.
 *
 * Note: this tracks **window** scroll only. Inner scroll containers
 * (e.g. an `overflow:auto` popup wrapper) do not fire — use a different
 * mechanism for those (see SCMPWEB-10011 plan §5).
 */
export const useScrollOverThreshold = (thresholdPx: null | number, label?: string): boolean => {
  // SSR-safe initial value: `false` on server AND on the client's hydration
  // render, so hydration diff is clean. The real scroll-derived value is synced
  // in the effect below via a microtask (avoids react-hooks/set-state-in-effect).
  const [isOver, setIsOver] = useState<boolean>(false);

  useEffect(() => {
    // Null / non-positive threshold = "threshold not measured yet" or "never
    // sticky" — keep the value at `false` and skip creating a sentinel.
    if (thresholdPx === null || thresholdPx <= 0) return;

    // Sync once on mount / threshold change to cover scroll-restored reloads:
    // the page may already be scrolled past the threshold when this hook mounts.
    // Microtask-deferred so we don't call setState synchronously inside the
    // effect body. The IO may also fire around the same time; both write the
    // same value, so ordering doesn't matter (last-write-wins on identical value).
    const current = readIsOver(thresholdPx);
    queueMicrotask(() => setIsOver(current));

    if (!supportsIntersectionObserver) return;

    const entry = getOrCreateEntry(thresholdPx, label);
    entry.subscribers.add(setIsOver);

    return () => releaseEntry(thresholdPx, setIsOver);
  }, [thresholdPx, label]);

  return isOver;
};
